// ============================================================================
// PITCH UI PRIMITIVES — pitchUi.jsx
// ============================================================================
// Small controls shared by the pitch types admin (pitchTypes.jsx) and the deck
// builder (pitchDeckBuilder.jsx): drag rows, toggles, number fields, a modal,
// and the ordered sections editor both screens show.
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, AlertTriangle } from 'lucide-react';
import { S, GOLD, AddLine, EmptyNote } from './commsUi';
import { SECTION_BY_KEY, SECTION_SCOPES, newSection, addableSections, describeSection } from '../../pitch/pitchSections';

export const MONO = "'IBM Plex Mono', monospace";
export const fieldLabel = { ...S.eyebrow, fontSize: 10, marginBottom: 6, display: 'block' };
export const selectStyle = { ...S.inputStyle, width: 'auto', minWidth: 160, cursor: 'pointer' };
export const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, display: 'inline-flex', alignItems: 'center' };

export function ToggleChip({ active, label, onClick, disabled, title }) {
  return (
    <button onClick={disabled ? undefined : onClick} title={title} style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 9px', borderRadius: 2, cursor: disabled ? 'default' : 'pointer', background: active ? '#DDE5DF' : 'transparent', border: active ? '1px solid #33473F' : '1px solid rgba(27,27,25,.14)', color: active ? '#21302A' : '#6E6C66' }}>
      {label}
    </button>
  );
}

// A number input that saves on blur, like InlineInput does for text.
export function NumberField({ value, onSave, min = 0, width = 72, placeholder }) {
  const [local, setLocal] = useState(value == null ? '' : String(value));
  useEffect(() => { setLocal(value == null ? '' : String(value)); }, [value]);
  return (
    <input
      type="number" min={min} value={local} placeholder={placeholder}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { const n = local === '' ? null : Number(local); if (n !== (value == null ? null : Number(value))) onSave(n); }}
      style={{ ...S.inputStyle, width }}
    />
  );
}

export function SortableRow({ id, disabled, style, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, ...style }}>
      {children({ attributes, listeners })}
    </div>
  );
}

export function useDragSensors() {
  return useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
}

export function Modal({ title, onClose, children, width = 880 }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(27,27,25,.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#F7F6F2', borderRadius: 4, width: '100%', maxWidth: width, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid rgba(27,27,25,.12)' }}>
          <h3 style={S.h3}>{title}</h3>
          <button onClick={onClose} style={{ ...iconBtn, fontSize: 18, color: '#6E6C66' }} title="Close">×</button>
        </div>
        <div style={{ padding: 18, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

// Ordered list of deck sections. `flag` on an entry (set by the engine when
// the content behind a section is empty) shows as a warning.
export function SectionsEditor({ sections, themes, canEdit, onChange }) {
  const sensors = useDragSensors();
  const themesById = useMemo(() => Object.fromEntries((themes || []).map(t => [t.comms_id, t])), [themes]);
  const ids = sections.map(s => s.id);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onChange(arrayMove(sections, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  const patch = (id, fields) => onChange(sections.map(s => s.id === id ? { ...s, ...fields } : s));
  const patchSettings = (id, fields) => onChange(sections.map(s => s.id === id ? { ...s, settings: { ...(s.settings || {}), ...fields } } : s));
  const remove = id => onChange(sections.filter(s => s.id !== id));
  const addable = addableSections(sections);

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {sections.map(sec => {
            const def = SECTION_BY_KEY[sec.key];
            return (
              <SortableRow key={sec.id} id={sec.id} disabled={!canEdit} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(27,27,25,.08)', opacity: sec.enabled ? 1 : 0.55 }}>
                {({ attributes, listeners }) => (
                  <>
                    <span {...attributes} {...listeners} style={{ cursor: canEdit ? 'grab' : 'default', color: '#B0A9A0', display: 'inline-flex', touchAction: 'none' }} title="Drag to reorder"><GripVertical size={14} /></span>
                    <input type="checkbox" checked={sec.enabled} disabled={!canEdit} onChange={e => patch(sec.id, { enabled: e.target.checked })} style={{ accentColor: GOLD }} title={sec.enabled ? 'Included' : 'Left out'} />
                    <span style={{ flex: 1, fontSize: 14, color: '#1B1B19', minWidth: 0 }} title={def ? def.description : ''}>{describeSection(sec, themesById)}</span>
                    {sec.flag && <span title={sec.flag} style={{ color: '#B8742A', display: 'inline-flex' }}><AlertTriangle size={13} /></span>}
                    {def && def.settings && def.settings.includes('theme_id') && (
                      <select value={sec.settings?.theme_id || ''} disabled={!canEdit} onChange={e => patchSettings(sec.id, { theme_id: e.target.value || null })} style={{ ...selectStyle, minWidth: 0, maxWidth: 190, flex: '0 1 190px' }}>
                        <option value="">Choose a theme…</option>
                        {(themes || []).map(t => <option key={t.comms_id} value={t.comms_id}>{t.label}</option>)}
                      </select>
                    )}
                    {def && def.settings && def.settings.includes('scope') && (
                      <select value={sec.settings?.scope || 'selected'} disabled={!canEdit} onChange={e => patchSettings(sec.id, { scope: e.target.value })} style={{ ...selectStyle, minWidth: 0, maxWidth: 190, flex: '0 1 190px' }}>
                        {SECTION_SCOPES.map(sc => <option key={sc.key} value={sc.key}>{sc.label}</option>)}
                      </select>
                    )}
                    {canEdit && <button onClick={() => remove(sec.id)} title="Remove section" style={iconBtn}><Trash2 size={13} /></button>}
                  </>
                )}
              </SortableRow>
            );
          })}
        </SortableContext>
      </DndContext>
      {sections.length === 0 && <EmptyNote text="No sections. The deck would be empty." />}
      {canEdit && addable.length > 0 && (
        <AddLine>
          <select value="" onChange={e => { if (e.target.value) onChange([...sections, newSection(e.target.value)]); }} style={selectStyle}>
            <option value="">Add a section…</option>
            {addable.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </AddLine>
      )}
    </div>
  );
}

export function Stars({ n }) {
  if (!n) return null;
  return <span style={{ color: GOLD, fontSize: 11, letterSpacing: 1 }} title={`${n} star`}>{'★'.repeat(n)}</span>;
}

// Category options: the registry labels, lowercased to match public.projects.category,
// plus anything a project carries that the registry does not list.
export function categoriesFrom(registryRows, projects) {
  const seen = new Map();
  (registryRows || []).forEach(r => { if (r.label) seen.set(r.label.toLowerCase(), r.label); });
  (projects || []).forEach(p => { const c = (p.typology || '').toLowerCase(); if (c && !seen.has(c)) seen.set(c, p.typology); });
  return Array.from(seen, ([key, label]) => ({ key, label }));
}
